import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BaseApiResponse,
  CompanySealResponse,
  CreateCompanySealRequest,
  PinStatusResponse,
  SetupPinRequest,
  VerifyPinRequest,
} from "~/types/common";

const KEY = ["business-seal"] as const;
const PIN_KEY = ["business-seal-pin-status"] as const;

/* ─── 도장 목록 ─── */
const fetchSealList = async (): Promise<CompanySealResponse[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<CompanySealResponse[]>>(
    API_ENDPOINTS.BUSINESS_SEAL.LIST
  );
  return data.data ?? [];
};

/**
 * 도장 목록 조회.
 *
 * `/api/business/**` 는 BUSINESS·TEACHER 롤 전용이라 보호자 토큰으로 호출하면 403 이고,
 * axios 응답 인터셉터가 그 403 을 "토큰 만료" 로 오인해 리프레시를 태운다(실패 시 로그아웃).
 * 그래서 사업자 화면이 아닌 컨텍스트(보호자 서명 화면이 재사용하는 편집기 등)에서는
 * `enabled: false` 로 호출 자체를 막는다.
 */
export const useSealList = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: KEY,
    queryFn: fetchSealList,
    enabled: options?.enabled ?? true,
  });

/* ─── 도장 등록 (multipart) ─── */
export const useCreateSeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { data: CreateCompanySealRequest; image: File }) => {
      const fd = new FormData();
      const dataBlob = new Blob([JSON.stringify(params.data)], {
        type: "application/json",
      });
      fd.append("data", dataBlob);
      fd.append("image", params.image);
      const { data } = await privateHttp.post<BaseApiResponse<CompanySealResponse>>(
        API_ENDPOINTS.BUSINESS_SEAL.CREATE,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("[seal create] response", data);
      if (data.statusCode !== 200) {
        throw new Error(
          `도장 등록 실패 (statusCode=${data.statusCode}${
            data.message ? ` / ${data.message}` : ""
          })`
        );
      }
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
};

/* ─── 기본 도장 변경 ─── */
export const useSetDefaultSeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sealSeq: number) => {
      await privateHttp.patch(API_ENDPOINTS.BUSINESS_SEAL.DEFAULT(sealSeq));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

/* ─── 활성 토글 ─── */
export const useToggleSealActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sealSeq: number) => {
      await privateHttp.patch(API_ENDPOINTS.BUSINESS_SEAL.ACTIVE(sealSeq));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

/* ─── 도장 삭제 ─── */
export const useDeleteSeal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sealSeq: number) => {
      await privateHttp.delete(API_ENDPOINTS.BUSINESS_SEAL.DELETE(sealSeq));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
};

/* ─── PIN 상태 ─── */
const fetchPinStatus = async (): Promise<PinStatusResponse> => {
  const { data } = await privateHttp.get<BaseApiResponse<PinStatusResponse>>(
    API_ENDPOINTS.BUSINESS_SEAL.PIN_STATUS
  );
  return data.data;
};

export const usePinStatus = () =>
  useQuery({ queryKey: PIN_KEY, queryFn: fetchPinStatus });

/* ─── PIN 등록/변경 ─── */
export const useSetupPin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SetupPinRequest) => {
      // 서버는 실패해도 HTTP 200 + body.statusCode 로 결과 전달 (envelope 패턴).
      // 예전에는 body 를 검사하지 않아 currentPin 이 틀려도 성공 UI 가 뜨는 UX 버그가 있었음.
      // 이제 statusCode 를 확인해 실패면 throw → react-query onError 가 발동한다.
      const { data } = await privateHttp.post<BaseApiResponse<unknown>>(
        API_ENDPOINTS.BUSINESS_SEAL.PIN_SETUP,
        req
      );
      if (data.statusCode !== 200) {
        throw new Error(data.message ?? "현재 PIN 이 올바르지 않습니다.");
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PIN_KEY }),
  });
};

/* ─── PIN 검증 ─── */
export const useVerifyPin = () =>
  useMutation({
    mutationFn: async (req: VerifyPinRequest): Promise<boolean> => {
      const { data } = await privateHttp.post<BaseApiResponse<boolean>>(
        API_ENDPOINTS.BUSINESS_SEAL.PIN_VERIFY,
        req
      );
      return !!data.data;
    },
  });
